import React, {
  useEffect,
  useState,
  useMemo,
  forwardRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Spin } from 'antd';
import PropTypes from 'prop-types';

import OnTraccrEmpty from '../common/OnTraccrEmpty';

import AnalyticsTable from './AnalyticsTable';
import AnalyticsNumber from './AnalyticsNumber';
import AnalyticsChart from './AnalyticsChart';

import { getAnalyticsData } from './state/analytics.actions';

import { rangePresetToTimestamps } from './analytics.helpers';
import { INACCESSIBLE_BOARD_LABEL } from './analytics.constants';

const AnalyticsBody = forwardRef(({
  type,
  analyticsConfig = {},
  getAction = getAnalyticsData,
  projectId,
  cardId,
}, ref) => {
  const dispatch = useDispatch();
  const boards = useSelector((state) => state.boards.boards);
  const cardTemplates = useSelector((state) => state.boards.cardTemplates);
  const formTemplates = useSelector((state) => state.forms.templates);

  const {
    data = [],
    boardId,
    formTemplateId,
    dateRange,
    datePreset,
    dateBreakdown,
    fieldBreakdown,
    chartType,
    fieldId,
    aggregate,
    breakdown,
    groups,
    filterByFieldUpdatedTime,
    statusFilter,
    category,
  } = analyticsConfig;

  const [loading, setLoading] = useState();

  const isBoardInaccessible = Boolean(boardId && !boards[boardId]);

  const fieldTitle = useMemo(() => {
    switch (fieldId) {
      case 'hoursWorked':
        return 'Hours Worked';
      case 'timeInStatus':
        return 'Time in Status';
      case 'conversion':
        return 'Conversion Rate';
      case 'numberOfCards':
        return 'Number of Cards';
      case 'newCardsInStatus':
        return 'Number of New Cards in Status';
      case 'numberOfSubmissions':
        return 'Number of Submissions';
      default:
        break;
    }
    let realFieldId = fieldId;
    let columnKey = '';
    if (fieldId && fieldId.includes('/')) {
      [realFieldId, columnKey] = fieldId.split('/');
    }
    let ourSections = [];
    if (boardId) {
      const {
        [boardId]: { cardTypeId } = {},
      } = boards;
      const {
        [cardTypeId]: {
          fields: sections = [],
        } = {},
      } = cardTemplates;
      ourSections = sections;
    } else if (formTemplateId) {
      const {
        [formTemplateId]: {
          formData: {
            sections = [],
          } = {},
        } = {},
      } = formTemplates;
      ourSections = sections;
    }

    let ft;
    ourSections.forEach(({ fields = [] }) => {
      fields.forEach((field) => {
        const {
          id,
          configProps: { title: fieldName, columns = [] } = {},
          selectedType,
        } = field;
        if (id === realFieldId) {
          if (selectedType === 'table') {
            const ourCol = columns.find((col) => col.key === columnKey);
            const suffix = ourCol ? ` - ${ourCol.name}` : '';
            ft = `${fieldName}${suffix}`;
          } else {
            ft = fieldName;
          }
        }
      });
    });
    return ft;
  }, [boards, boardId, formTemplateId, cardTemplates, formTemplates, fieldId]);

  useEffect(() => {
    const isNumber = chartType === 'number';
    const isPie = chartType === 'pie';
    const isList = chartType === 'list';
    const getData = async () => {
      setLoading(true);
      const rangeFromPreset = rangePresetToTimestamps(datePreset);
      const [startMoment, endMoment] = dateRange ?? [];
      const payload = {
        boardId,
        formTemplateId,
        startTime: startMoment ? startMoment.valueOf() : rangeFromPreset[0],
        endTime: endMoment ? endMoment.valueOf() : rangeFromPreset[1],
        fieldId,
        aggregate,
        breakdown,
        chartType,
        groups: groups ?? {},
        projectId,
        cardId,
        filterByFieldUpdatedTime,
        statusFilter,
        category,
      };
      if (fieldBreakdown) {
        payload.fieldBreakdown = fieldBreakdown;
      } else {
        payload.dateBreakdown = isNumber || isPie || isList ? 'none' : dateBreakdown;
      }
      await dispatch(getAction({
        type,
        payload,
        category
      }));
      setLoading(false);
    };
    if (!isBoardInaccessible
        && (boardId || formTemplateId || category)
        && ((dateRange && dateRange.length === 2) || datePreset)
        && (dateBreakdown || fieldBreakdown || isNumber || isPie || isList)
        && fieldId
        && aggregate
        && chartType) {
      getData();
    }
  }, [
    dispatch,
    isBoardInaccessible,
    boardId,
    formTemplateId,
    dateRange,
    dateBreakdown,
    fieldBreakdown,
    fieldId,
    aggregate,
    breakdown,
    chartType,
    groups,
    datePreset,
    projectId,
    cardId,
    filterByFieldUpdatedTime,
    statusFilter,
    category,
  ]);

  const body = useMemo(() => {
    if (isBoardInaccessible) {
      return (
        <Row style={{ flex: 1 }} justify="center" align="middle">
          <OnTraccrEmpty title={INACCESSIBLE_BOARD_LABEL} showOnlyTitle />
        </Row>
      );
    }
    if (data.length === 0 || loading) {
      return (
        <Row style={{ flex: 1 }} justify="center" align="middle">
          {loading ? <Spin /> : <OnTraccrEmpty title="Analytics" />}
        </Row>
      );
    }
    if (chartType === 'table' || chartType === 'list') return <AnalyticsTable fieldTitle={fieldTitle} ref={ref} analyticsConfig={analyticsConfig} cardId={cardId} />;
    if (chartType === 'number') return <AnalyticsNumber fieldTitle={fieldTitle} ref={ref} analyticsConfig={analyticsConfig} cardId={cardId} />;
    return (
      <AnalyticsChart
        fieldTitle={fieldTitle}
        ref={ref}
        analyticsConfig={analyticsConfig}
        cardId={cardId}
      />
    );
  }, [data, loading, isBoardInaccessible, chartType, fieldTitle, ref, analyticsConfig, cardId]);

  return (
    <div className="analytics-body-container">
      {body}
    </div>
  );
});

/* eslint-disable react/forbid-prop-types */
AnalyticsBody.propTypes = {
  type: PropTypes.string,
  analyticsConfig: PropTypes.object,
  getAction: PropTypes.func,
  projectId: PropTypes.string,
  cardId: PropTypes.string,
};

AnalyticsBody.defaultProps = {
  type: undefined,
  analyticsConfig: {},
  getAction: getAnalyticsData,
  projectId: undefined,
  cardId: undefined,
};

export default AnalyticsBody;
